import React, { useEffect, useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { issueMessage } from "./publicationValidation";

export default function PublicationReadiness({ result, stale, checking, onCheck, onIssue, lang = "id" }) {
  const [expanded, setExpanded] = useState(false);
  const issues = result ? [...(result.errors || []), ...(result.warnings || [])] : [];
  const hasErrors = !!result?.error_count;
  const statusText = !result
    ? (lang === "id" ? "Belum diperiksa" : "Not checked")
    : stale
      ? (lang === "id" ? "Perlu diperiksa ulang" : "Needs recheck")
      : hasErrors
        ? (lang === "id" ? "Belum siap dipublikasikan" : "Not ready to publish")
        : (lang === "id" ? "Siap dipublikasikan" : "Ready to publish");
  const StatusIcon = !result || stale ? RefreshCw : hasErrors ? AlertCircle : CheckCircle2;

  useEffect(() => {
    if (result?.error_count || result?.warning_count) setExpanded(true);
  }, [result]);

  return (
    <section className="mb-6 rounded-2xl border border-border bg-card px-4 py-3" aria-label={lang === "id" ? "Kesiapan publikasi" : "Publication readiness"} data-testid="publication-readiness">
      <div className="flex flex-wrap items-center gap-3">
        <StatusIcon className={`h-4 w-4 shrink-0 ${hasErrors && !stale ? "text-destructive" : result && !stale ? "text-teal-700" : "text-muted-foreground"}`} aria-hidden="true" />
        <div className="min-w-44 flex-1">
          <div className="text-sm font-semibold">{lang === "id" ? "Kesiapan publikasi" : "Publication readiness"}</div>
          <div className="text-xs text-muted-foreground" data-testid="readiness-status">{statusText}</div>
        </div>
        {result && (
          <div className="flex items-center gap-2 text-xs" aria-label={`${result.error_count} errors, ${result.warning_count} warnings`}>
            <span className="rounded-full bg-destructive/10 px-2.5 py-1 font-medium text-destructive" data-testid="readiness-error-count">
              {result.error_count} {lang === "id" ? "error" : "errors"}
            </span>
            <span className="rounded-full bg-amber-100 px-2.5 py-1 font-medium text-amber-800" data-testid="readiness-warning-count">
              {result.warning_count} {lang === "id" ? "peringatan" : "warnings"}
            </span>
          </div>
        )}
        {issues.length > 0 && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} data-testid="readiness-toggle">
            {expanded ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
            {lang === "id" ? "Detail" : "Details"}
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={onCheck} disabled={checking} data-testid="readiness-check">
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${checking ? "animate-spin" : ""}`} />
          {checking ? (lang === "id" ? "Memeriksa…" : "Checking…") : (lang === "id" ? "Periksa kesiapan" : "Check readiness")}
        </Button>
      </div>

      {expanded && issues.length > 0 && (
        <div className="mt-3 max-h-72 space-y-2 overflow-y-auto border-t border-border pt-3" data-testid="readiness-issues">
          {issues.map((issue, index) => {
            const warning = issue.severity === "warning";
            return (
              <React.Fragment key={`${issue.code}-${issue.path}-${index}`}>
                {(index === 0 || issues[index - 1].severity !== issue.severity) && (
                  <div className={`px-1 pt-1 text-[10px] font-semibold uppercase tracking-widest ${warning ? "text-amber-700" : "text-destructive"}`}>
                    {warning ? (lang === "id" ? "Peringatan" : "Warnings") : (lang === "id" ? "Error" : "Errors")}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onIssue(issue)}
                  className="flex w-full items-start gap-2 rounded-xl border border-border bg-background px-3 py-2 text-left transition-colors hover:border-terracotta-300"
                  data-testid={`readiness-issue-${index}`}
                >
                  {warning
                    ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                    : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />}
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm">{issueMessage(issue, lang)}</span>
                    <span className="block truncate font-mono text-[10px] text-muted-foreground">{issue.path} · {issue.tab}</span>
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </section>
  );
}
